import { buildProductPhotos } from './productPhotos';
import { getProductVariants } from './productVariants';
import { VALID_COUNTRY_IDS } from './countryConfig';
import { buildProductExportDetails, getReferenceVariants } from './productExportDetails';
import { findProductPhotoReference } from './productPhotoReferences';
import { KOREA_EXPORT_COLUMNS, groupKoreaExportRows, koreaSalePrice, koreaNumericValues } from './koreaLivestreamExport';
import { productExportWeight } from './productExportWeight';

// Keep document-mode XLSX generation small enough for phones. There is no streaming
// image workbook in ExcelJS, so fail clearly rather than silently truncating rows.
export const LIVESTREAM_EXPORT_LIMITS = Object.freeze({
  maxRows: 2000,
  imageConcurrency: 3,
  imageTimeoutMs: 15000,
  maxSourceImageBytes: 8 * 1024 * 1024,
  maxImagePixels: 24 * 1024 * 1024,
  maxImageWidth: 320,
  maxImageHeight: 240,
  maxImageBytes: 100 * 1024,
  maxTotalImageBytes: 16 * 1024 * 1024,
});

export const LIVESTREAM_EXPORT_COLUMNS = Object.freeze([
  { key: 'image', label: '商品圖片（全部）', width: 20 },
  { key: 'sampleNumbers', label: '樣品編號', width: 24 },
  { key: 'itemNumber', label: 'SKU／料號', width: 20 },
  { key: 'name', label: '品名', width: 32 },
  { key: 'styles', label: '已選款式', width: 28 },
  { key: 'cost', label: '成本（含幣別）', width: 32 },
  { key: 'effects', label: '功效', width: 40 },
  { key: 'locations', label: '樣品位置', width: 30 },
  { key: 'barcodes', label: '款式／條碼', width: 34 },
  { key: 'prices', label: '直播價／商城價', width: 32, detail: true },
  { key: 'productPrices', label: '商品原始售價', width: 32, detail: true },
  { key: 'vendor', label: '廠商', width: 24, detail: true },
  { key: 'remarks', label: '備註', width: 40, detail: true },
  { key: 'weight', label: '重量', width: 24, detail: true, numeric: true },
  { key: 'detailCost', label: '商品／款式成本', width: 36, detail: true },
  { key: 'comboCost', label: '組合合計成本', width: 36, detail: true },
  { key: 'convertedCost', label: '換算／總成本', width: 40, detail: true },
  { key: 'shipping', label: '運費計算', width: 40, detail: true },
  { key: 'costBreakdown', label: '成本／運費組成與公式', width: 48, detail: true },
  { key: 'rateBasis', label: '換算與運費依據', width: 36, detail: true },
  { key: 'subProducts', label: '子商品', width: 40, detail: true },
  { key: 'gifts', label: '贈品', width: 40, detail: true },
].map(Object.freeze));

const COST_FIELDS = Object.freeze([
  ['costTWD', 'TWD'],
  ['costKoreanWon', 'KRW'],
  ['costJPY', 'JPY'],
  ['costTHB', 'THB'],
  ['costRMB', 'RMB'],
]);
const text = value => value === null || value === undefined ? '' : String(value).trim();
const unique = values => Array.from(new Set(values));
const isPresent = value => value !== null && value !== undefined && text(value) !== '';
const abortError = () => Object.assign(new Error('已取消匯出'), { name: 'AbortError' });
const checkAbort = signal => { if (signal?.aborted) throw abortError(); };

export const getExportCostText = source => COST_FIELDS
  .filter(([field]) => isPresent(source?.[field]))
  .map(([field, currency]) => `${text(source[field])} ${currency}`)
  .join(' / ');

export const normalizeExportColumns = columns => {
  const keys = columns === undefined
    ? LIVESTREAM_EXPORT_COLUMNS.map(column => column.key)
    : columns;
  const selected = new Set(Array.isArray(keys) ? keys : []);
  return LIVESTREAM_EXPORT_COLUMNS.filter(column => selected.has(column.key));
};

const koreaCostKeys = ['costKRW', 'costTWD', 'adjustedTWD', 'airTWD', 'seaTWD', 'transitTWD'];
const koreaLegacyColumns = {
  prices: ['productPrices'], cost: ['costKRW', 'costTWD'], detailCost: ['costKRW', 'costTWD'], comboCost: ['costKRW', 'costTWD'],
  convertedCost: ['adjustedTWD'], shipping: ['adjustedTWD', 'airTWD', 'seaTWD', 'transitTWD'], costBreakdown: koreaCostKeys, rateBasis: koreaCostKeys,
};
export const getLivestreamExportColumns = country => country !== 'korea' ? LIVESTREAM_EXPORT_COLUMNS : [
  ...LIVESTREAM_EXPORT_COLUMNS.filter(c => !Object.hasOwn(koreaLegacyColumns, c.key) && c.key !== 'rateBasis'),
  ...KOREA_EXPORT_COLUMNS.filter(c => koreaCostKeys.includes(c.key)).map(c => ({...c,detail:true})),
];
const koreaColumns = selected => {
  const keys = selected ?? LIVESTREAM_EXPORT_COLUMNS.map(c => c.key);
  const normalized = new Set(keys.flatMap(key => koreaLegacyColumns[key] || [key]));
  return getLivestreamExportColumns('korea').filter(c => normalized.has(c.key)).map(c => c.key === 'productPrices' ? {...c,numeric:true} : c);
};

/** Materialize every displayed field synchronously, before fetching any image. */
export const buildLivestreamExportSnapshot = ({ rows = [], selectedColumns, listName = '', format = 'full' } = {}) => {
  if (!Array.isArray(rows) || !rows.length) throw new Error('清單沒有可匯出的商品');
  if (rows.length > LIVESTREAM_EXPORT_LIMITS.maxRows) {
    throw new Error(`每次最多匯出 ${LIVESTREAM_EXPORT_LIMITS.maxRows} 個商品，請分成較小的清單`);
  }
  if (format === 'korea-simple') {
    const groups = groupKoreaExportRows(rows);
    const base = buildLivestreamExportSnapshot({ rows: groups, listName, selectedColumns: ['image', 'itemNumber', 'name', 'styles', 'remarks'] });
    const issues = [...base.issues];
    const numericColumns = KOREA_EXPORT_COLUMNS.filter(c => c.numeric);
    const grouped = base.rows.map((row, rowIndex) => {
      const values = { ...row.values, ...groups[rowIndex].koreaNumeric };
      const missing = numericColumns.filter(c => values[c.key] === null).map(c => c.label);
      if (missing.length) issues.push(Object.freeze({ rowIndex, label: row.label, code: 'missing_numeric', message: `缺少或無效的成本、售價、重量或費率，以下欄位留白：${missing.join('、')}` }));
      if (groups[rowIndex].koreaWeightIssue) issues.push(Object.freeze({rowIndex,label:row.label,code:'weight_ambiguous',message:groups[rowIndex].koreaWeightIssue}));
      return Object.freeze({ ...row, weight: groups[rowIndex].koreaWeight, weightGroup: groups[rowIndex].koreaWeightGroup, values: Object.freeze(Object.fromEntries(KOREA_EXPORT_COLUMNS.map(c => [c.key, values[c.key]]))) });
    });
    return Object.freeze({ ...base, format, columns: KOREA_EXPORT_COLUMNS, rows: Object.freeze(grouped), issues: Object.freeze(issues) });
  }
  const korea = rows.every(r => (r.country || r.product?.country) === 'korea');
  const columns = korea ? koreaColumns(selectedColumns) : normalizeExportColumns(selectedColumns);
  if (!columns.length) throw new Error('請至少選擇一個匯出欄位');
  const selected = new Set(columns.map(column => column.key));
  const issues = [];
  const countries = new Set(rows.map(r => r.country || r.product?.country).filter(Boolean));
  if (countries.size > 1 || [...countries].some(c => !VALID_COUNTRY_IDS.includes(c))) throw new Error('不可混合不同國家的商品匯出');
  const costRows = korea && columns.some(c => koreaCostKeys.includes(c.key)) ? groupKoreaExportRows(rows) : rows;
  const weightedGroups = new Set();
  const exportedRows = costRows.map((entry, index) => {
    const product = entry?.product || {};
    const country = entry.country || product.country;
    if (entry.country && product.country !== undefined && entry.country !== product.country) throw new Error('商品國家不符');
    if (country && entry.samples?.some(s => s.country !== undefined && s.country !== country)) throw new Error('樣品國家不符');
    const label = text(product.name) || text(product.itemNumber) || `第 ${index + 1} 個商品`;
    const addIssue = (code, message) => issues.push(Object.freeze({ rowIndex: index, label, code, message }));
    const allVariants = getProductVariants(product);
    const selectedIds = new Set(Array.isArray(entry?.selectedVariantIds) ? entry.selectedVariantIds : []);
    const variants = allVariants.filter(variant => selectedIds.has(variant.id));
    if (selected.has('weight')) {
      const weight = productExportWeight(product, variants, entry.detailContext);
      if (weight.issue) addIssue('weight_ambiguous', weight.issue);
    }
    const knownIds = new Set(allVariants.map(variant => variant.id));
    if (Array.from(selectedIds).some(id => !knownIds.has(id))) addIssue('stale_variants', '部分已選款式已不存在，請重新確認款式');
    if (allVariants.length && !variants.length) addIssue('missing_variants', '未選擇有效款式');
    const samples = (Array.isArray(entry?.samples) ? entry.samples : [])
      .filter(sample => sample && (!sample.variantId || selectedIds.has(sample.variantId)));
    const multiCost = product.enableMultiCostWeight === true || (
      product.enableMultiCostWeight === undefined && allVariants.some(variant => getExportCostText(variant))
    );
    const cost = multiCost
      ? variants.map(variant => {
        const value = getExportCostText(variant);
        if (!value && selected.has('cost')) addIssue('missing_cost', `${text(variant.name) || '未命名款式'}：缺少成本`);
        return value ? `${text(variant.name) || '未命名款式'}：${value}` : '';
      }).filter(Boolean).join('\n')
      : getExportCostText(product);
    if (!multiCost && !cost && selected.has('cost')) addIssue('missing_cost', '缺少成本');
    if (!text(product.name)) addIssue('missing_name', '缺少品名');
    if (!text(product.itemNumber)) addIssue('missing_item_number', '缺少 SKU／料號');
    if (!text(product.effects) && selected.has('effects')) addIssue('missing_effects', '缺少功效');
    if (selected.has('sampleNumbers') && (!samples.length || samples.some(sample => !text(sample.number)))) {
      addIssue('missing_sample_number', samples.length ? '有實體樣品尚未編號' : '尚無實體樣品編號');
    }
    if (selected.has('locations') && (!samples.length || samples.some(sample => !text(sample.location)))) {
      addIssue('missing_location', '有樣品尚未填寫位置');
    }
    const barcodeLines = variants.map(variant => {
      const value = text(variant.rawBarcode) || text(variant.barcode);
      if (!value && selected.has('barcodes')) addIssue('missing_barcode', `${text(variant.name) || '未命名款式'}：缺少條碼`);
      return value ? `${text(variant.name) || '未命名款式'}：${value}` : '';
    }).filter(Boolean);
    const variantNames = new Map(allVariants.map(variant => [variant.id, text(variant.name)]));
    const sampleLabel = sample => {
      const variant = sample.variantId ? variantNames.get(sample.variantId) : '';
      return `${text(sample.number) || '未編號'}${variant ? `（${variant}）` : ''}`;
    };
    const excludedImages = new Set(allVariants.filter(v => !selectedIds.has(v.id)).map(v => v.image).filter(Boolean));
    variants.forEach(v => excludedImages.delete(v.image));
    const photos = selected.has('image') ? buildProductPhotos({
      ...product,
      photos: (Array.isArray(product.photos) ? product.photos : []).filter(p => !excludedImages.has(typeof p === 'string' ? p : p.url)),
      styles: variants.map(variant => variant.name),
      styleDetails: variants,
    }).sort((a, b) => Number(b.isCover) - Number(a.isCover)).map(p => ({ url: p.url, label: p.label || '商品照片', reference: findProductPhotoReference(product, country, p.url) })) : [];
    if (selected.has('image')) for (const kind of ['subProducts', 'gifts']) {
      if (!selected.has(kind)) continue;
      (product[kind] || []).forEach((ref, i) => {
        const related = entry.detailContext?.[kind]?.[i]?.product;
        if (!related) return;
        const chosen = getReferenceVariants(ref, related, i, kind);
        for (const v of chosen.length ? chosen : [{}]) {
          const url = v.image || related.image;
          if (url) photos.push({ url, label: `${kind === 'gifts' ? '贈品' : '子商品'}：${ref.itemNumber} ${v.name || ''}`, reference: findProductPhotoReference(related, country, url) });
        }
      });
    }
    const imageUrl = photos[0]?.url || '';
    if (selected.has('image') && !photos.length) addIssue('missing_image', '缺少商品圖片');
    for (const kind of ['subProducts', 'gifts']) if (selected.has(kind)) entry.detailContext?.[kind]?.forEach((ref, i) => { if (ref.error) addIssue('related_missing', `${kind === 'gifts' ? '贈品' : '子商品'}第 ${i + 1} 項：${ref.error}`); });
    const omitWeight = entry.koreaWeightGroup !== undefined && weightedGroups.has(entry.koreaWeightGroup);
    if (entry.koreaWeightGroup !== undefined) weightedGroups.add(entry.koreaWeightGroup);
    if (entry.koreaNumeric) for (const column of columns.filter(c => koreaCostKeys.includes(c.key))) {
      if (entry.koreaNumeric[column.key] === null) addIssue('missing_numeric', `${column.label}缺少成本或費率，已留白`);
    }
    const values = Object.freeze({
      image: '',
      sampleNumbers: samples.map(sampleLabel).join('\n'),
      itemNumber: text(product.itemNumber),
      name: text(product.name),
      styles: variants.map(variant => text(variant.name)).join('/'),
      cost,
      effects: text(product.effects),
      locations: samples.filter(sample => text(sample.location))
        .map(sample => `${sampleLabel(sample)}：${text(sample.location)}`).join('\n'),
      barcodes: barcodeLines.join('\n') || (!allVariants.length ? text(product.rawBarcode || product.barcode) : ''),
      ...Object.fromEntries(Object.entries(buildProductExportDetails(entry, variants)).filter(([key]) => !['country', 'history', 'photoHistory', 'memberships'].includes(key))),
      ...(country === 'korea' ? { productPrices: koreaSalePrice(product, variants) } : {}),
      ...(entry.koreaNumeric ? {...entry.koreaNumeric, productPrices:entry.koreaNumeric.salePrice, weight:omitWeight ? null : entry.koreaWeight} : {}),
    });
    // Long fields are split into lossless continuation cells in the workbook.
    const relatedCosts = country === 'korea' ? ['subProducts','gifts'].filter(kind=>selected.has(kind)).flatMap(kind=>(product[kind] || []).flatMap((ref,i)=>{
      const related=entry.detailContext?.[kind]?.[i]?.product;
      if(!related)return [];
      const choices=getReferenceVariants(ref,related,i,kind);
      return (choices.length ? choices : [{}]).map(variant=>({kind:kind==='gifts'?'贈品':'子商品',itemNumber:ref.itemNumber,name:related.name,style:variant.name || '',quantity:ref.quantity ?? 1,values:koreaNumericValues({product:related,detailContext:entry.detailContext},variant)}));
    })) : [];
    return Object.freeze({ productId: text(product.id), label, imageUrl, photos: Object.freeze(photos.map(Object.freeze)), omitWeight, relatedCosts, values });
  });
  return Object.freeze({
    listName: text(listName),
    createdAt: new Date().toISOString(),
    columns: Object.freeze(columns.slice()),
    rows: Object.freeze(exportedRows),
    issues: Object.freeze(issues),
  });
};

const readBoundedBlob = async (response, maxBytes, signal) => {
  const length = Number(response.headers?.get('content-length'));
  if (length > maxBytes) throw new Error('原圖超過 8 MB 限制');
  const contentType = (response.headers?.get('content-type') || '').split(';')[0].toLowerCase();
  if (contentType && !['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'application/octet-stream'].includes(contentType)) {
    throw new Error('圖片格式不支援，請使用 JPEG、PNG、WebP、GIF 或 BMP');
  }
  if (!response.body?.getReader) {
    // Browser fetch normally supplies a stream. Do not read an unbounded body in
    // old browsers when the server omits its size.
    if (!Number.isFinite(length) || length <= 0) throw new Error('瀏覽器無法安全讀取未提供大小的圖片');
    const blob = await response.blob();
    if (blob.size > maxBytes) throw new Error('原圖超過 8 MB 限制');
    return blob;
  }
  const reader = response.body.getReader();
  const parts = [];
  let total = 0;
  try {
    while (true) {
      checkAbort(signal);
      const { done, value } = await reader.read();
      if (done) break;
      total += value.byteLength;
      if (total > maxBytes) throw new Error('原圖超過 8 MB 限制');
      parts.push(value);
    }
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
  return new Blob(parts, { type: contentType });
};

const decodeImage = async (blob, signal) => {
  checkAbort(signal);
  if (typeof createImageBitmap === 'function') {
    return new Promise((resolve, reject) => {
      const onAbort = () => reject(abortError());
      signal?.addEventListener('abort', onAbort, { once: true });
      createImageBitmap(blob).then(image => {
        signal?.removeEventListener('abort', onAbort);
        if (signal?.aborted) { image.close(); reject(abortError()); return; }
        resolve({ image, width: image.width, height: image.height, release: () => image.close() });
      }, error => {
        signal?.removeEventListener('abort', onAbort);
        reject(error);
      });
    });
  }
  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(blob);
    const cleanup = () => {
      image.onload = null;
      image.onerror = null;
      signal?.removeEventListener('abort', onAbort);
      URL.revokeObjectURL(url);
    };
    const onAbort = () => { cleanup(); image.src = ''; reject(abortError()); };
    image.onload = () => {
      cleanup();
      resolve({ image, width: image.naturalWidth, height: image.naturalHeight, release: () => { image.src = ''; } });
    };
    image.onerror = () => { cleanup(); reject(new Error('圖片無法解碼')); };
    signal?.addEventListener('abort', onAbort, { once: true });
    image.src = url;
  });
};

/** Fetch anonymously, cap both source and decoded size, then embed a small JPEG. */
export const loadBoundedExportImage = async (url, { signal } = {}) => {
  url = String(url).trim().replace(/^data:image\/(jpg|pjpeg);/i, 'data:image/jpeg;').replace(/^data:image\/x-png;/i, 'data:image/png;');
  if (!/^(https?:\/\/|blob:|data:image\/(?:png|jpeg|webp|gif|bmp);base64,)/i.test(url)) {
    throw new Error('圖片網址或格式不支援');
  }
  if (url.startsWith('data:') && url.length > LIVESTREAM_EXPORT_LIMITS.maxSourceImageBytes * 4 / 3 + 200) {
    throw new Error('原圖超過 8 MB 限制');
  }
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  checkAbort(signal);
  signal?.addEventListener('abort', onAbort, { once: true });
  const timer = setTimeout(() => controller.abort(), LIVESTREAM_EXPORT_LIMITS.imageTimeoutMs);
  let decoded;
  let phase = 'download';
  try {
    const response = await fetch(url, { signal: controller.signal, mode: 'cors', credentials: 'omit' });
    if (!response.ok) throw new Error(`圖片讀取失敗（HTTP ${response.status}）`);
    const blob = await readBoundedBlob(response, LIVESTREAM_EXPORT_LIMITS.maxSourceImageBytes, controller.signal);
    checkAbort(controller.signal);
    phase = 'decode';
    decoded = await decodeImage(blob, controller.signal);
    checkAbort(controller.signal);
    phase = 'encode';
    if (!decoded.width || !decoded.height || decoded.width * decoded.height > LIVESTREAM_EXPORT_LIMITS.maxImagePixels) {
      throw new Error('圖片尺寸過大或無效');
    }
    const ratio = Math.min(1, LIVESTREAM_EXPORT_LIMITS.maxImageWidth / decoded.width, LIVESTREAM_EXPORT_LIMITS.maxImageHeight / decoded.height);
    const width = Math.max(1, Math.round(decoded.width * ratio));
    const height = Math.max(1, Math.round(decoded.height * ratio));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('瀏覽器不支援圖片轉換');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(decoded.image, 0, 0, width, height);
    let dataUrl;
    let byteLength;
    for (const quality of [0.82, 0.65, 0.45]) {
      dataUrl = canvas.toDataURL('image/jpeg', quality);
      byteLength = Math.ceil((dataUrl.split(',')[1]?.length || 0) * 3 / 4);
      if (byteLength <= LIVESTREAM_EXPORT_LIMITS.maxImageBytes) break;
    }
    canvas.width = 0;
    canvas.height = 0;
    if (!dataUrl?.startsWith('data:image/jpeg;base64,') || !byteLength || byteLength > LIVESTREAM_EXPORT_LIMITS.maxImageBytes) {
      throw new Error('圖片壓縮後仍超過 100 KB 限制');
    }
    return Object.freeze({ dataUrl, extension: 'jpeg', byteLength, width, height });
  } catch (error) {
    if (signal?.aborted) throw abortError();
    if (controller.signal.aborted) throw new Error('圖片讀取逾時，請重試');
    if (phase === 'download' && error instanceof TypeError && /^https?:/i.test(url)) throw new Error('瀏覽器無法取得遠端圖片內容，可能是網路或來源的跨來源讀取限制；圖片網址已保留，請交由系統管理員檢查來源回應');
    if (phase === 'decode' && (error instanceof TypeError || error.name === 'InvalidStateError' || /decode/i.test(error.message || ''))) throw new Error('已取得圖片內容，但瀏覽器無法解碼；請交由系統管理員檢查來源格式');
    throw error;
  } finally {
    decoded?.release();
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  }
};

/** No download occurs here: callers must display these issues before writing. */
export const prepareLivestreamExport = async ({
  rows, selectedColumns, listName, format = 'full', authorized = false, signal, onProgress,
  imageLoader = loadBoundedExportImage,
} = {}) => {
  if (!authorized) throw new Error('只有管理員與員工可以匯出清單');
  checkAbort(signal);
  const snapshot = buildLivestreamExportSnapshot({ rows, selectedColumns, listName, format });
  const issues = [...snapshot.issues];
  const urls = unique(snapshot.rows.flatMap(row => row.photos.map(photo => photo.url)).filter(Boolean));
  const references = new Map(snapshot.rows.flatMap(row => row.photos.map(photo => [photo.url, photo.reference])));
  const images = new Map();
  let next = 0;
  let completed = 0;
  let totalBytes = 0;
  let fatalError = null;
  const failures = new Map();
  const worker = async () => {
    while (next < urls.length && !fatalError) {
      const url = urls[next++];
      checkAbort(signal);
      try {
        const image = await imageLoader(url, { signal, reference: references.get(url) });
        checkAbort(signal);
        if (!image || !['jpeg', 'png'].includes(image.extension) || !/^data:image\/(jpeg|png);base64,/.test(image.dataUrl || '') || !image.byteLength || image.byteLength > LIVESTREAM_EXPORT_LIMITS.maxImageBytes || !Number.isFinite(image.width) || !Number.isFinite(image.height) || image.width <= 0 || image.height <= 0 || image.width > LIVESTREAM_EXPORT_LIMITS.maxImageWidth || image.height > LIVESTREAM_EXPORT_LIMITS.maxImageHeight) {
          throw new Error('圖片格式或大小不符合匯出限制');
        }
        if (totalBytes + image.byteLength > LIVESTREAM_EXPORT_LIMITS.maxTotalImageBytes) {
          fatalError = new Error('圖片總量超過 16 MB，無法完整匯出。請縮小清單或取消圖片欄位後重試');
          throw fatalError;
        }
        totalBytes += image.byteLength;
        images.set(url, Object.freeze({ ...image }));
      } catch (error) {
        if (error.fatal) fatalError = error;
        if (fatalError) throw fatalError;
        if (signal?.aborted || error.name === 'AbortError') throw abortError();
        failures.set(url, error.message || '圖片讀取失敗');
      }
      completed += 1;
      onProgress?.({ completed, total: urls.length });
    }
  };
  await Promise.all(Array.from({ length: Math.min(urls.length, LIVESTREAM_EXPORT_LIMITS.imageConcurrency) }, worker));
  checkAbort(signal);
  const preparedRows = snapshot.rows.map((row, rowIndex) => {
    const photos = row.photos.map((photo, photoIndex) => {
      const error = failures.get(photo.url) || '';
      if (error) issues.push(Object.freeze({ rowIndex, photoIndex, label: row.label, code: 'image_failed', message: `第 ${photoIndex + 1} 張（${photo.label}）：${error}` }));
      return Object.freeze({ ...photo, image: images.get(photo.url) || null, error });
    });
    return Object.freeze({ ...row, photos: Object.freeze(photos), images: Object.freeze(photos.filter(p => p.image)), image: images.get(row.imageUrl) || null });
  });
  return Object.freeze({ ...snapshot, rows: Object.freeze(preparedRows), issues: Object.freeze(issues), totalImageBytes: totalBytes });
};

export const createLivestreamWorkbook = async (prepared, { authorized = false, signal } = {}) => {
  if (!authorized) throw new Error('只有管理員與員工可以匯出清單');
  checkAbort(signal);
  if (!prepared?.rows?.length || !prepared?.columns?.length) throw new Error('請先完成匯出檢查');
  const excelModule = await import('exceljs');
  const Workbook = excelModule.Workbook || excelModule.default?.Workbook;
  const workbook = new Workbook();
  workbook.creator = 'BARCODE';
  workbook.created = new Date(prepared.createdAt);
  workbook.modified = new Date(prepared.createdAt);
  const summaryColumns = prepared.columns;
  const sheet = workbook.addWorksheet('直播準備清單', {
    views: [{ state: 'frozen', ySplit: 1, xSplit: Math.min(4, Math.max(0, summaryColumns.length - 1)), zoomScale: 100 }],
    properties: { defaultRowHeight: 36 },
    // A fully selected table is deliberately wide. Print across pages at normal
    // text size instead of shrinking every column onto one unreadable page.
    pageSetup: { paperSize: summaryColumns.reduce((n, c) => n + c.width, 0) > 180 ? 8 : 9, orientation: 'landscape', fitToPage: false, scale: 100 },
  });
  sheet.columns = (summaryColumns.length ? summaryColumns : [{ label: '商品', key: '_label', width: 32 }]).map(column => ({ header: column.label, key: column.key, width: column.width }));
  sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: prepared.rows.length + 1, column: Math.max(summaryColumns.length, 1) } };
  sheet.pageSetup.printTitlesRow = '1:1';
  const skuIndex = summaryColumns.findIndex(c => c.key === 'itemNumber');
  if (skuIndex >= 0) {
    const lastTitleIndex = summaryColumns[skuIndex + 1]?.key === 'name' ? skuIndex + 1 : skuIndex;
    sheet.pageSetup.printTitlesColumn = `${sheet.getColumn(skuIndex + 1).letter}:${sheet.getColumn(lastTitleIndex + 1).letter}`;
  }
  const header = sheet.getRow(1);
  header.height = 28;
  header.eachCell(cell => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF234A5C' } };
    cell.font = { name: 'Microsoft JhengHei', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.alignment = { vertical: 'middle', wrapText: true };
  });
  const imageColumnIndex = summaryColumns.findIndex(column => column.key === 'image');
  const imageIds = new Map();
  const outputLabel = (entry, index) => [prepared.columns.some(c => c.key === 'itemNumber') ? entry.values.itemNumber : '', prepared.columns.some(c => c.key === 'name') ? entry.values.name : ''].filter(Boolean).join(' · ') || `商品 ${index + 1}`;
  const embed = (target, image, col, row, maxWidth, maxHeight) => {
    if (!imageIds.has(image.dataUrl)) imageIds.set(image.dataUrl, workbook.addImage({ base64: image.dataUrl, extension: image.extension }));
    const ratio = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
    // Keep the drawing inside the cell: a zero offset can bleed onto the previous printed page in Excel.
    target.addImage(imageIds.get(image.dataUrl), { tl: { nativeCol: col, nativeColOff: 19050, nativeRow: row, nativeRowOff: 19050 }, ext: { width: Math.round(image.width * ratio), height: Math.round(image.height * ratio) }, editAs: 'oneCell' });
  };
  const needsDetail = (value, column) => wrapExportText(value, Math.max(6, Math.floor(column.width - 2))).length > 5;
  const detailNeeded = prepared.format !== 'korea-simple' && (prepared.columns.some(c => c.detail) || prepared.rows.some(r => summaryColumns.some(c => needsDetail(r.values[c.key] || '', c))));
  prepared.rows.forEach((entry, index) => {
    checkAbort(signal);
    const row = sheet.addRow(summaryColumns.length ? summaryColumns.map(column => {
      const value = entry.values[column.key] ?? '';
      return typeof value === 'string' && value.length > 32767 ? value.slice(0, /[\uD800-\uDBFF]/.test(value[32766]) ? 32766 : 32767) : value;
    }) : [outputLabel(entry, index)]);
    const lines = Math.max(1, ...summaryColumns.map((c, i) => wrapExportText(row.getCell(i + 1).value || '', Math.max(6, Math.floor(c.width - 2))).length));
    row.height = Math.min(360, Math.max(imageColumnIndex >= 0 ? 82 : 48, lines * 15 + 10));
    row.eachCell({ includeEmpty: true }, (cell, columnNumber) => {
      // Text type + @ is intentional, including costs that can contain multiple
      // currencies/variants. It also protects leading zeros and formula-like data.
      cell.numFmt = summaryColumns[columnNumber - 1]?.numeric && typeof cell.value === 'number' ? '0.###############' : '@';
      if (summaryColumns[columnNumber - 1]?.numeric && cell.value === '') cell.value = null;
      cell.font = { name: 'Microsoft JhengHei', size: 11, color: { argb: 'FF172B35' } };
      cell.alignment = { vertical: 'middle', wrapText: true };
      cell.border = { bottom: { style: 'hair', color: { argb: 'FFDCE5E9' } } };
      if (index % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F7F9' } };
    });
    if (imageColumnIndex >= 0 && entry.image) {
      embed(sheet, entry.image, imageColumnIndex, row.number - 1, 120, 94);
    } else if (imageColumnIndex >= 0) {
      row.getCell(imageColumnIndex + 1).value = '';
    }
  });
  const setup = (name, columns) => {
    const s = workbook.addWorksheet(name, { views: [{ state: 'frozen', ySplit: 1, showGridLines: false }], pageSetup: { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0, printTitlesRow: '1:1', margins: { left: 0.25, right: 0.25, top: 0.4, bottom: 0.4, header: 0.15, footer: 0.15 } } });
    s.columns = columns.map(([header, width]) => ({ header, width }));
    s.getRow(1).height = 26;
    s.getRow(1).eachCell(c => { c.font = { name: 'Microsoft JhengHei', size: 11, bold: true, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF234A5C' } }; });
    return s;
  };
  const addTextRow = (s, values, height = 32) => {
    const r = s.addRow(values); r.height = height;
    r.eachCell({ includeEmpty: true }, c => { c.numFmt = '@'; c.font = { name: 'Microsoft JhengHei', size: 11 }; c.alignment = { vertical: 'top', wrapText: true }; c.border = { bottom: { style: 'hair', color: { argb: 'FFDCE5E9' } } }; });
    return r;
  };
  if (prepared.format === 'korea-simple') {
    const costs = setup('費用明細', [['料號', 20], ['品名', 30], ['已選樣式', 24], ['項目', 30], ['數值', 20], ['單位', 12]]);
    const weightedProducts = new Set();
    for (const entry of prepared.rows) {
      for (const [key, label, unit] of [['weight', '重量', ''], ['costKRW','韓幣成本','KRW'], ['costTWD','台幣成本','TWD'], ['adjustedTWD', '台幣換算＋額外10%', 'TWD'], ['airTWD', '直飛空運', 'TWD'], ['seaTWD', '海快', 'TWD'], ['transitTWD', '轉機空運', 'TWD']]) {
        if (key === 'weight') {
          const group = entry.weightGroup ?? entry.productId;
          if (weightedProducts.has(group)) continue;
          weightedProducts.add(group);
        }
        const value = key === 'weight' ? entry.weight : entry.values[key];
        const row = addTextRow(costs, [entry.values.itemNumber, entry.values.name, key === 'weight' ? '' : entry.values.styles, label, value ?? null, key === 'weight' ? '' : unit]);
        row.getCell(5).numFmt = '0.###############';
      }
    }
  }
  if (detailNeeded) {
    const detail = setup('商品詳情', [['商品／欄位', 26], ['內容', 72]]);
    for (const [index, entry] of prepared.rows.entries()) {
      checkAbort(signal);
      const title = addTextRow(detail, [outputLabel(entry, index), ''], 30); title.font = { name: 'Microsoft JhengHei', bold: true, size: 12 };
      for (const column of prepared.columns.filter(c => c.key !== 'image')) {
        if (column.key === 'weight' && entry.omitWeight) continue;
        if (column.numeric) {
          const row = addTextRow(detail, [column.label, entry.values[column.key] ?? null]);
          row.getCell(2).numFmt = typeof row.getCell(2).value === 'number' ? '0.###############' : '@';
          continue;
        }
        const lines = wrapExportText(entry.values[column.key] || '', 62);
        for (let i = 0; i < lines.length; i += 18) addTextRow(detail, [column.label + (i ? '（續）' : ''), lines.slice(i, i + 18).join('\n')], Math.max(28, Math.min(360, lines.slice(i, i + 18).length * 18 + 8)));
      }
    }
  }
  if (prepared.rows.some(entry=>entry.relatedCosts?.length)) {
    const fields=KOREA_EXPORT_COLUMNS.filter(c=>koreaCostKeys.includes(c.key));
    const relatedSheet=setup('關聯商品成本',[['商品',26],['關係',12],['關聯料號',20],['品名',30],['樣式',20],['數量',12],...fields.map(c=>[c.label,c.width])]);
    prepared.rows.forEach((entry,index)=>{if(entry.omitWeight)return;for(const ref of entry.relatedCosts || []){
      const row=addTextRow(relatedSheet,[outputLabel(entry,index),ref.kind,ref.itemNumber,ref.name,ref.style,ref.quantity,...fields.map(c=>ref.values[c.key]??null)]);
      fields.forEach((_,i)=>{row.getCell(i+7).numFmt='0.###############';});
    }});
  }
  const longFields = prepared.rows.flatMap((entry, index) => prepared.columns.filter(c => c.key !== 'image' && typeof entry.values[c.key] === 'string' && entry.values[c.key].length > 32767).map(column => ({ entry, index, column })));
  if (longFields.length) {
    const raw = setup('完整長文', [['商品', 26], ['欄位', 24], ['段次', 26], ['原文', 72]]);
    for (const { entry, index, column } of longFields) {
      const value = entry.values[column.key];
      for (let start = 0, part = 1; start < value.length; part++) {
        let end = Math.min(start + 30000, value.length);
        if (end < value.length && /[\uD800-\uDBFF]/.test(value[end - 1])) end--;
        addTextRow(raw, [outputLabel(entry, index), column.label, String(part), value.slice(start, end)], 360);
        start = end;
      }
    }
  }
  if (imageColumnIndex >= 0) {
    const album = setup('商品圖片', [['商品', 26], ['照片', 24], ['圖片', 44]]);
    // Excel may split a floating drawing at automatic page boundaries. Three
    // photo rows per A4 page keep the whole drawing inside the printable area.
    album.pageSetup.fitToPage = false;
    album.pageSetup.scale = 100;
    let photoRowsOnPage = 0;
    for (const [index, entry] of prepared.rows.entries()) {
      checkAbort(signal);
      for (const [i, photo] of (entry.photos || []).entries()) {
        if (photoRowsOnPage === 3) { album.lastRow.addPageBreak(); photoRowsOnPage = 0; }
        const row = addTextRow(album, [outputLabel(entry, index), `${i + 1}. ${photo.label}`, ''], photo.image ? 166 : 70);
        if (photo.image) embed(album, photo.image, 2, row.number - 1, 280, 210);
        photoRowsOnPage += 1;
      }
    }
  }
  return workbook;
};

// Excel caps row height at 409 points. Split long paragraphs into continuation
// rows instead of shrinking text or silently hiding content below that limit.
export const wrapExportText = (value, units = 62) => String(value).split('\n').flatMap(line => {
  const result = []; let buffer = '', width = 0;
  for (const character of line) {
    const size = character.codePointAt(0) > 255 ? 2 : 1;
    if (width + size > units && buffer) { result.push(buffer); buffer = ''; width = 0; }
    buffer += character; width += size;
  }
  result.push(buffer); return result;
});

export const buildLivestreamXlsx = async (prepared, options) => {
  const workbook = await createLivestreamWorkbook(prepared, options);
  checkAbort(options?.signal);
  // XML normalizes literal CR characters, and Excel interprets _xNNNN_. Encode
  // both explicitly so notes survive an XLSX round trip without altered text.
  workbook.eachSheet(sheet => sheet.eachRow(row => row.eachCell(cell => {
    if (typeof cell.value === 'string') cell.value = cell.value.replace(/_x[0-9a-fA-F]{4}_/g, match => '_x005F_' + match.slice(1)).replace(/\r/g, '_x000D_')
      // Escape surrogate pairs as SpreadsheetML code units. This also avoids
      // legacy XLSX readers splitting a UTF-8 emoji at an internal chunk edge.
      .replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, pair => [...Array(2)].map((_, i) => `_x${pair.charCodeAt(i).toString(16).toUpperCase()}_`).join(''));
  })));
  const buffer = await workbook.xlsx.writeBuffer();
  checkAbort(options?.signal);
  return buffer;
};

export const downloadLivestreamXlsx = (buffer, listName = '') => {
  const safeName = (text(listName) || '直播準備清單').replace(/[\\/:*?"<>|]/g, '_').split('').map(character => character.charCodeAt(0) < 32 ? '_' : character).join('').slice(0, 100);
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${safeName}.xlsx`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};
